import { useEffect, useMemo, useState } from 'react';
import { V4 } from '../../../tokens';
import type { UploadQueueItem } from '../types';

export interface ProcessingProgressProps {
  done: number;
  total: number;
  failed: number;
  items: UploadQueueItem[];
}

/**
 * Fase visual del batch. Con single-call no hay progreso incremental honesto
 * entre 0 y N archivos, pero SÍ hay dos fases reales y secuenciales:
 * subida a Storage (segundos) → análisis LLM (minutos). El hook transiciona
 * los items vía `onUploadsComplete`, y aquí se derivan para el copy y la barra.
 */
type BatchPhase = 'uploading' | 'analyzing' | 'failed' | 'done';

const ANALYZING_MESSAGES = [
  'Leyendo tus imágenes…',
  'Detectando categorías…',
  'Extrayendo platillos y precios…',
  'Armando secciones y extras…',
  'Consolidando duplicados…',
];

const UPLOADING_MESSAGES = ['Subiendo tus archivos…'];

const ROTATION_MS = 5000;
const TICK_MS = 1000;

function derivePhase(items: UploadQueueItem[], failed: number): BatchPhase {
  if (items.some((i) => i.status === 'analyzing')) return 'analyzing';
  if (items.some((i) => i.status === 'uploading' || i.status === 'queued')) return 'uploading';
  if (failed > 0) return 'failed';
  return 'done';
}

function buildCounterText(phase: BatchPhase, total: number): string {
  const plural = total === 1 ? '' : 's';
  switch (phase) {
    case 'failed':
      return `Falló el procesamiento de ${total} archivo${plural}`;
    case 'uploading':
      return `Paso 1 de 2 · Subiendo ${total} archivo${plural}…`;
    case 'analyzing':
      return 'Paso 2 de 2 · Analizando tu menú con IA…';
    default:
      return `${total} archivo${plural} listo${plural}`;
  }
}

function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function ProcessingProgress({ done, total, failed, items }: ProcessingProgressProps) {
  const phase = derivePhase(items, failed);
  const inProgress = phase === 'uploading' || phase === 'analyzing';

  const [messageIndex, setMessageIndex] = useState(0);
  // Inicio del batch: el startedAt más antiguo de los items, o el mount como
  // fallback. Se fija una sola vez — el timer es informativo, no exacto.
  const [startMs] = useState<number>(() => {
    const starts = items.map((i) => i.startedAt).filter((t): t is number => typeof t === 'number');
    return starts.length > 0 ? Math.min(...starts) : Date.now();
  });
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const messages = phase === 'uploading' ? UPLOADING_MESSAGES : ANALYZING_MESSAGES;

  useEffect(() => {
    if (!inProgress) return;
    const id = window.setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ANALYZING_MESSAGES.length);
    }, ROTATION_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [inProgress]);

  useEffect(() => {
    if (!inProgress) return;
    const id = window.setInterval(() => {
      setNowMs(Date.now());
    }, TICK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [inProgress]);

  const safeTotal = total > 0 ? total : 1;
  const percent = useMemo(() => {
    if (phase === 'failed') return 100;
    return Math.min(100, Math.max(0, (done / safeTotal) * 100));
  }, [phase, done, safeTotal]);

  return (
    <div
      data-testid="processing-progress"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: '100%',
      }}
    >
      {/* Keyframes de la barra indeterminada. prefers-reduced-motion la congela
          en un fill estático semitransparente (la actividad la comunica el copy). */}
      <style>{`
        @keyframes smov4-indeterminate-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .smov4-indeterminate-fill {
          animation: smov4-indeterminate-slide 1.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .smov4-indeterminate-fill {
            animation: none;
            transform: none;
            width: 100% !important;
            opacity: 0.35;
          }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: V4.primary,
          }}
        >
          Procesando tu menú
        </h3>
        {inProgress ? (
          <span
            data-testid="processing-progress-elapsed"
            style={{
              fontSize: 12,
              fontVariantNumeric: 'tabular-nums',
              color: V4.grayUltraDark,
            }}
          >
            {formatElapsed(nowMs - startMs)}
          </span>
        ) : null}
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        // Indeterminada mientras procesa (sin aria-valuenow, per ARIA);
        // determinada solo en estados terminales.
        {...(inProgress ? {} : { 'aria-valuenow': Math.round(percent) })}
        style={{
          width: '100%',
          height: 8,
          background: V4.grayLight,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {inProgress ? (
          <div
            data-testid="processing-progress-bar-indeterminate"
            className="smov4-indeterminate-fill"
            style={{
              width: '25%',
              height: '100%',
              background: V4.secondary,
              borderRadius: 4,
            }}
          />
        ) : (
          <div
            data-testid="processing-progress-bar-fill"
            style={{
              width: `${percent}%`,
              height: '100%',
              background: phase === 'failed' ? V4.danger : V4.secondary,
              transition: 'width 300ms ease',
            }}
          />
        )}
      </div>

      <div
        data-testid="processing-progress-counter"
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: phase === 'failed' ? V4.danger : V4.grayUltraDark,
        }}
      >
        {buildCounterText(phase, total)}
      </div>

      {inProgress ? (
        <>
          <div
            aria-live="polite"
            style={{
              fontSize: 12,
              color: V4.grayUltraDark,
              fontStyle: 'italic',
              minHeight: 16,
            }}
          >
            {messages[messageIndex % messages.length]}
          </div>
          <div
            style={{
              fontSize: 12,
              color: V4.grayUltraDark,
              lineHeight: 1.5,
            }}
          >
            Los menús grandes pueden tardar varios minutos. No cierres esta pantalla.
          </div>
        </>
      ) : null}
    </div>
  );
}
